import { SignInButton } from "@clerk/react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { clerkEnabled } from "../auth";
import { trpc } from "../trpc";

import { Loading } from "./StateViews";

function GateShell({
  eyebrow,
  title,
  children,
  cta,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
  cta?: ReactNode;
}) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-5 pt-28 text-center sm:px-8">
      <span className="font-deva text-5xl leading-none text-terracotta">अ</span>
      <p className="eyebrow mt-6">{eyebrow}</p>
      <h1 className="mt-3 font-serif text-4xl text-ink">{title}</h1>
      <div className="mt-3 leading-relaxed text-ink-soft">{children}</div>
      {cta ? <div className="mt-7">{cta}</div> : null}
      <Link
        to="/glossary"
        className="mt-6 text-sm font-medium text-terracotta hover:underline"
      >
        Or explore the glossary →
      </Link>
    </div>
  );
}

/**
 * Gate a personal / member-only screen. Public reading stays open to
 * everyone. Two walls: sign in, then be approved by a community admin —
 * a signed-in but pending account can't capture, study, or contribute.
 */
export function RequireAuth({
  children,
  action = "continue",
}: {
  children: ReactNode;
  action?: string;
}) {
  const me = trpc.session.me.useQuery();

  if (me.isLoading) return <Loading label="One moment" />;

  if (!me.data) {
    return (
      <GateShell
        eyebrow="A study of the Swarveda"
        title={`Sign in to ${action}`}
        cta={
          clerkEnabled ? (
            <SignInButton mode="modal">
              <button
                type="button"
                className="rounded-full bg-ink px-6 py-2.5 text-sm font-medium text-paper transition-opacity hover:opacity-90"
              >
                Sign in
              </button>
            </SignInButton>
          ) : undefined
        }
      >
        Reading the glossary, the graph and the dohas is open to everyone. To
        photograph the couplets you’re reading and keep your own study, sign in
        first.
      </GateShell>
    );
  }

  if (!me.data.approved) {
    return (
      <GateShell
        eyebrow="Almost there"
        title="Your account is awaiting approval"
      >
        You’re signed in{me.data.email ? ` as ${me.data.email}` : ""}. To keep
        the shared study trustworthy, a community admin approves each member
        before they can capture, annotate, and contribute. You’ll have full
        access as soon as you’re approved — reading stays open in the meantime.
      </GateShell>
    );
  }

  return <>{children}</>;
}
